import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { ParamContext } from '../../service/param.context';
import { Observable } from 'rxjs';
import { SERVER_API_URL } from '../../../app.constants';
import { CreatePreviewCrossesRequestModel } from '../model/create-preview-crosses-request.model';
import { PreviewCrossesModel } from '../model/preview-crosses.model';
import { PreviewCrossesRequestModel } from '../model/preview-crosses.request.model';
import { GermplasmDto } from '../../germplasm/model/germplasm.model';
import { CrossNamePresetSetting, SaveCrossesRequestModel } from '../model/save-crosses.request.model';
import { ImportCrossesRequestModel } from '../model/import-crosses.request.model';
import { ImportCrossesResponseModel } from '../model/import-crosses.response.model';
import { EnableLoadingIndicator, EnablePageBlocking, EnableSavingIndicator } from '../../loading-indicator';
import { PreviewImportedCrossesModel } from '../model/preview-imported-crosses.model';

@Injectable({ providedIn: 'root' })
export class CrossService {
    private baseUrl = SERVER_API_URL;

    constructor(
        private http: HttpClient,
        private context: ParamContext
    ) {
    }

    createPreviewCrosses(studyId: number, request: CreatePreviewCrossesRequestModel): Observable<PreviewCrossesModel[]> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses/generate-preview`;
        return this.http.post<PreviewCrossesModel[]>(url, request);
    }

    getPreviewCross(studyId: number, request: PreviewCrossesRequestModel): Observable<PreviewCrossesModel[]> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses/preview`;
        return this.http.post<PreviewCrossesModel[]>(url, request);
    }

    saveCrosses(studyId: number, request: SaveCrossesRequestModel): Observable<PreviewCrossesModel[]> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses`;
        return this.http.post<PreviewCrossesModel[]>(url, request, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    getExistingCrosses(femaleGIDs: number[], maleGIDs: number[]): Observable<GermplasmDto[]> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/crosses/existing/femaleGIDs/${femaleGIDs}/maleGIDs/${maleGIDs}`;
        return this.http.get<GermplasmDto[]>(url);
    }

    getHybridMethods(): Observable<number[]> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/crosses/hybrid-methods`;
        return this.http.get<number[]>(url);
    }

    validateBreedingMethod(crossMethodRequest: any): Observable<void> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/crosses/validate/breeding-method`;
        return this.http.post<void>(url, crossMethodRequest);
    }

    validateBreedingMethodForImport(request: ImportCrossesRequestModel): Observable<void> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/crosses/germplasm/validate/breeding-method`;
        return this.http.post<void>(url, request);
    }

    addSeedSourceToPreviewCrosses(studyId: number, request: PreviewImportedCrossesModel[]): Observable<void> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses/seed-source`;
        return this.http.post<void>(url, request);
    }

    getAllPresets(toolSection): Observable<CrossNamePresetSetting[]> {
        const options: HttpParams = new HttpParams()
            .append('toolId', '23')
            .append('toolSection', toolSection);

        const resourceUrl = `crops/${this.context.cropName}/programs/${this.context.programUUID}/presets`;
        return this.http.get<CrossNamePresetSetting[]>(this.baseUrl + resourceUrl, {
            params: options,
        });
    }

    addPreset(preset: CrossNamePresetSetting): Observable<CrossNamePresetSetting> {
        const resourceUrl = `crops/${this.context.cropName}/programs/${this.context.programUUID}/presets`;
        return this.http.put<CrossNamePresetSetting>(this.baseUrl + resourceUrl, preset);
    }

    updatePreset(preset: CrossNamePresetSetting): Observable<void> {
        const resourceUrl = `crops/${this.context.cropName}/programs/${this.context.programUUID}/presets/${preset.id}`;
        return this.http.put<void>(this.baseUrl + resourceUrl, preset);
    }

    deletePreset(presetId: number): Observable<CrossNamePresetSetting> {
        const resourceUrl = `crops/${this.context.cropName}/programs/${this.context.programUUID}/presets/${presetId}`;
        return this.http.delete<CrossNamePresetSetting>(this.baseUrl + resourceUrl);
    }

    importFile(studyId: number, crossImportRequest: any): Observable<ImportCrossesResponseModel> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses/germplasm`;
        return this.http.post<ImportCrossesResponseModel>(url, crossImportRequest, { context: new HttpContext().set(EnableLoadingIndicator, true).set(EnablePageBlocking, true) });
    }

    downloadCrossingTemplate(studyId: number) {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/studies/${studyId}/crosses/templates/xlsx`;
        return this.http.get(url,
            {
                responseType: 'blob',
                observe: 'response',
                context: new HttpContext().set(EnableLoadingIndicator, true)
            });
    }
}
